import * as Sentry from "@sentry/react";
import {
  setWith,
  cloneDeep,
  mergeWith,
  isObject,
  isNil,
  omitBy,
  isArray,
  set,
  get,
  has,
  isEqual,
  forEach,
} from "lodash";
import { produce } from "immer";
import { resolveBackendBaseUrl } from "./backendResolution";

/**
 * Creates a slug / identifier from a string, that is a valid key in Firebase.
 * @param {string} input input string
 * @param {object} opts
 * @param {boolean} [opts.allowUnderscore] if true, will allow underscores in the slug. Only allowed in claim keys.
 * @param {boolean} [opts.allowDashes] if true, will allow dashes in the slug. Allowed in scenarioIds but not organizationIds.
 * @param {boolean} [opts.lowerCase] if false, keeps the original casing instead of lowercasing.
 * @returns {string} a slug
 */
export const slugifyKey = function (input, { allowUnderscore = false, allowDashes = true, lowerCase = true } = {}) {
  const whitespaces = allowUnderscore ? /[\s]+/g : /[_\s]+/g;
  let slug = input
    .trim()
    .replace(whitespaces, "-")
    .replace(/[|<>"?*:\\.$[\]#/@]/g, "")
    // Don't allow ids that are only numbers, they will mess upp Firebase by being interpreted as lists not objects
    .replace(/^(\d+)$/g, "no$1");

  slug = (lowerCase ? slug.toLowerCase() : slug) || "-";
  if (!allowDashes) {
    slug = slug.replace(/-/g, "");
  }
  return slug;
};

/**
 * Converts a slug or filename to a human-readable title, safely handling undefined or non-string input.
 * @param {string} input
 * @returns {string}
 */
export const titleifySlug = function (input) {
  if (typeof input !== "string" || !input) return "";
  // Replace file ending
  let title = input.replace(/\.[^/.]{1,}$/, "");

  // Replace hyphens and underscores with spaces
  title = title.replace(/[-_]/g, " ");

  // Split the title into words, capitalize the first letter of each word, and join them back together
  title = title
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return title;
};

export const orgIdFromDomain = domain => {
  domain = domain.trim();
  try {
    const url = new URL(domain);
    domain = url.hostname;
  } catch (err) {
    // Not a URL, assume it's a domain name
  }
  // Important, we don't allow dashes in organization ids, because sometimes we rely on splitting the
  // scenario ID by first dash to find the organization ID. This is not a safe way to do it, but
  // we have to keep it for backwards compatibility. In future, it would be better to merge parts of an ID with _
  return { domain, id: slugifyKey(domain, { allowDashes: false }) };
};

/**
 * Checks if a unit timestamp is still valid based on the current time and a validity period.
 * @param {*} unixTime unix timestamp as a number or stringified number
 * @param {*} validityMs validity period in ms
 * @returns true if the timestamp plus validity is more than current timestamp, false otherwise
 */
export const isUnixTimeValid = (unixTime, validityMs) => {
  return parseInt(unixTime || 0) + validityMs > new Date().getTime();
};

export const isoDateToUnixTime = isoDate => {
  return isoDate ? new Date(isoDate).getTime() : 0;
};

export const isISODateValid = (isoDate, validitySec) => {
  return new Date(isoDate).getTime() + validitySec * 1000 > new Date().getTime();
};

// ISO-string input
export const formatDate = d => {
  if (d && d.length !== "") {
    // Converts ISOstring back to date object "weekday month day year time".
    // Then converting the date obj to a string
    let newDate = new Date(d).toString();
    // Slice to remove time from the  string and splitting on space to recieve [weekday,month,day,year]
    let dateArr = newDate.slice(0, 21).split(" ");
    return `${dateArr[2]} ${dateArr[1]} ${dateArr[3]} ${dateArr[4]}`;
  } else {
    return "-";
  }
};

export const isoDateString = d => {
  if (!d) return "-";
  return new Date(d).toISOString().substring(0, 10);
};

const isoDateTimeRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?(([+-]\d{2}:\d{2})|Z)?$/;

export const isValidISODate = d => {
  return isoDateTimeRegex.test(d);
};

export const getUserName = (users, userId) => {
  if (!userId) return "Unknown";
  return (users || []).find(user => user.id === userId)?.displayName || "Unknown";
};

export const startSpan = ({ spanRef, op, description }) => {
  if (spanRef.current) return; // SpanRef contains span already
  const span = Sentry.startInactiveSpan({ name: description, op });
  spanRef.current = span;
};

export const endSpan = (spanRef, ok = true) => {
  if (!spanRef.current) {
    console.log(`No span started when called to end`);
  } else {
    spanRef.current.end();
    if (ok) {
      spanRef.current.setStatus("ok");
    } else {
      spanRef.current.setStatus("unknown_error");
    }
    spanRef.current = null;
  }
};

export const getTraceHeader = () => {
  const activeSpan = Sentry.getActiveSpan();
  const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined;

  // Return `sentry-trace` header
  return rootSpan ? Sentry.spanToTraceHeader(rootSpan) : "";
};

/**
 * Returns the hostname for a URL-like input.
 * Accepts values with or without protocol.
 * @param {string} urlLike
 * @returns {string}
 */
export const getHostname = urlLike => {
  if (typeof urlLike !== "string") return "";
  const raw = urlLike.trim();
  if (!raw) return "";

  try {
    return new URL(raw).hostname.toLowerCase();
  } catch (e) {
    // Try again with protocol for inputs like "example.com/path"
    try {
      return new URL(`https://${raw}`).hostname.toLowerCase();
    } catch (e2) {
      return "";
    }
  }
};

/**
 * Old backend URLs are hosted on *.engine.fictivereality.com.
 * @param {string} serverUrl
 * @returns {boolean}
 */
export const isOldBackendUrl = serverUrl => {
  return getHostname(serverUrl).endsWith("engine.fictivereality.com");
};

/**
 *
 * @param {object} settings
 * @param {string} path
 * @param {boolean} backup
 * @param {boolean} logResolution
 * @returns
 */
export const getBackendUrl = ({ settings, path, backup = false, logResolution = false } = {}) => {
  const localBackendUrl = import.meta.env.VITE_LOCAL_BACKEND_URL || null;
  const {
    alias: resolvedAlias,
    baseUrl: resolvedBaseUrl,
    reason: resolutionReason,
  } = resolveBackendBaseUrl({
    defaultBackendChannel: import.meta.env.VITE_DEFAULT_BACKEND_CHANNEL,
    requestedBackendChannel: settings?.backendChannel,
    customServerUrl: settings?.customServerUrl?.trim(),
    backup,
    localBackendUrl,
  });

  if (logResolution) {
    console.info(
      `[getBackendUrl] alias=${resolvedAlias || "none"} baseUrl=${resolvedBaseUrl} path=${path || "-"} reason=${resolutionReason}`
    );
  }

  if (path) {
    return joinPaths(resolvedBaseUrl, path);
  } else {
    return resolvedBaseUrl;
  }
};

/**
 * Checks whether the backend resolved from merged settings points to the old vocode backend.
 * @param {object} settings
 * @returns {boolean}
 */
export const isOldBackendSettings = settings => {
  return isOldBackendUrl(getBackendUrl({ settings, logResolution: false }));
};

export const compose = (...funcs) =>
  funcs.reduce(
    (a, b) =>
      (...args) =>
        a(b(...args)),
    arg => arg
  );

/**
 * For a path a/b/?c the search will be empty so we have to fallback to checking if its in the path
 * @param {*} locationObject
 * @returns
 */
export const getQueryArgs = locationObject => {
  if (!locationObject) locationObject = window.location;
  if (locationObject?.search) {
    return new URLSearchParams(locationObject.search);
  } else if (locationObject?.pathname?.includes("?")) {
    return new URLSearchParams(locationObject.pathname.split("?")[1]);
  } else {
    return null;
  }
};

export const cloneAndSet = (obj, keyPath, value) => {
  // Deep clone to ensure nested objects are plain mutable objects.
  // Shallow copy would leave nested references that could be non-extensible/frozen
  // and cause setWith to throw when adding/deleting properties.
  let newObj = cloneDeep(obj) || {};
  setWith(newObj, keyPath, value, Object);
  return newObj;
};

export const lodashToFirebaseUpdateObject = (obj, extraObj = {}) => {
  let updateObject = {};
  for (const [path, value] of Object.entries(obj || {})) {
    // Firebase update dicts uses / as path separator, lodash uses .
    updateObject[path.replaceAll(".", "/")] = value;
  }
  return { ...updateObject, ...extraObj };
};

/**
 * Compares an old and a new object and returns an object with the differences, as
 * a Firebase style update object. Changed arrays will be replaced completely.
 * @param {*} oldObject
 * @param {*} newObject
 * @param {string} prefix
 * @returns {object} updateObject
 */
export const createUpdateObject = (oldObject, newObject, prefix = "") => {
  let updateObject = {};
  oldObject = oldObject || {};
  newObject = newObject || {};

  // Handle additions and changes
  forEach(newObject, (value, key) => {
    const fullPath = prefix ? `${prefix}/${key}` : key;
    if (isObject(value) && !isArray(value) && has(oldObject, key)) {
      const nestedUpdates = createUpdateObject(oldObject[key], value, fullPath);
      updateObject = { ...updateObject, ...nestedUpdates };
    } else if (!isEqual(oldObject[key], value)) {
      updateObject[fullPath] = value === undefined ? null : value;
    }
  });

  // Handle deletions by checking keys present in old but not in new
  forEach(oldObject, (value, key) => {
    const fullPath = prefix ? `${prefix}/${key}` : key;
    if (!has(newObject, key)) {
      updateObject[fullPath] = null;
    }
  });

  return updateObject;
};

export const sleep = ms => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const mergeRules = (a, b) => {
  if (isNil(b)) {
    return a;
  } else if (isObject(a) && isObject(b)) {
    a = mergeWith(a, b, (c, d) => (isNil(d) ? c : d));
    return a;
  } else {
    return undefined; // Default merge strategy, e.g. right overwrites left
  }
};

export const mergeSettings = settingsArray => {
  if (!settingsArray) return {};
  let merged = cloneDeep(settingsArray[0] || {});
  for (let s of settingsArray.slice(1)) {
    // Merge each setting in turn into the merged object
    mergeWith(merged, s, mergeRules);
  }
  // Clean merge from possible null values
  merged = omitBy(merged, isNil);
  return merged;
};

export const numberOrNull = value => {
  let num = Number(value);
  return isNaN(num) ? null : num;
};

/**
 * Rounds a number to N significant digits.
 * Integer inputs are returned unchanged.
 *
 * @param {number} value
 * @param {number} digits
 * @returns {number}
 */
export const roundToSignificantDigits = (value, digits = 2) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return value;
  if (Number.isInteger(value)) return value;

  const safeDigits = Number.isInteger(digits) && digits > 0 ? digits : 2;
  const rounded = Number.parseFloat(value.toPrecision(safeDigits));
  return Object.is(rounded, -0) ? 0 : rounded;
};

/**
 * This will remove all tags from the html that are not in the allowedTags array.
 * NOTE this is not a secure way to sanitize HTML, and that should be done on the server.
 * This is just to remove certain tags before rendering.
 *
 * @param {*} html
 * @param {*} allowedTags
 * @returns
 */
export const whitelistedHtml = (html, allowedTags) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  function walk(node) {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        if (!allowedTags.includes(child.tagName.toLowerCase())) {
          child.remove();
        } else {
          walk(child);
        }
      }
    }
  }

  walk(doc.body);

  return { __html: doc.body.innerHTML };
};

const VALID_ID_CHARS = "-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz";

/**
 * We try to repair a firebase ID so that it's either valid or empty.
 * @param {string} id
 */
export const repairFirebaseId = id => {
  let newId = id?.slice(0, 20) || "";
  // Check that newId only contains valid characters
  if (newId.length < 13 || newId.match(`[^${VALID_ID_CHARS}]`)) {
    // If we have less than 13 chars or invalid characters, we can't repair it
    newId = "";
  } else if (newId.length < 20) {
    // Try to repair the ID by mimicing how Firebase generates the first part of the ID
    // (from a timestamp 60 days ago, e.g. when the invite could probably have been created)
    // See https://gist.github.com/mikelehen/3596a30bd69384624c11 for ID generation details
    var now = new Date().getTime();

    var timeStampChars = new Array(8);
    for (var i = 7; i >= 0; i--) {
      timeStampChars[i] = VALID_ID_CHARS.charAt(now % 64);
      // NOTE: Can't use << here because javascript will convert to int and lose the upper bits.
      now = Math.floor(now / 64);
    }
    var guessedId = timeStampChars.join("");

    newId = guessedId.slice(0, 20 - newId.length) + newId;
  }
  return newId;
};

export const toSelect = (value, options) => {
  if (isArray(value)) {
    return value.map(item => options.find(option => option.value === item));
  } else {
    return options.find(option => option.value === value);
  }
};

export const applyUpdateObject = (target, updateObj) => {
  const newObj = produce(target, draft => {
    for (const [path, value] of Object.entries(updateObj || {})) {
      // Firebase update dicts uses / as path separator, lodash uses .
      set(draft, path.replaceAll("/", "."), value);
    }
  });

  // Immer returns frozen objects, other code expects mutable objects
  return cloneDeep(newObj);
};

export const getNextKeyNumber = obj => {
  const keys = Object.keys(obj);
  let maxNumber = 0;

  keys.forEach(key => {
    const match = key.match(/(\d+)$/);
    if (match) {
      const number = parseInt(match[1], 10);
      if (number > maxNumber) {
        maxNumber = number;
      }
    }
  });

  return maxNumber + 1;
};

const getParentIds = (map, id, parentIds = [], parentField = "parent") => {
  const obj = map[id];
  if (!obj) return parentIds;
  const parentId = get(obj, parentField);
  if (!parentId) return parentIds;
  if (parentIds.includes(parentId)) {
    console.warn(`Circular reference detected in ${parentField} for ${id}`);
    return [];
  } else {
    parentIds.push(parentId);
    return getParentIds(map, parentId, parentIds, parentField);
  }
};

const NUM_PADDING = 5;

/**
 * Sorts an object map into a list. The objects in the map may refer to each other
 * e.g. through a `parent` field. The parent field is used to determine the hierarchy.
 * The sorting order is based on each objects `order` property, but added together with
 * the parent's order, so that it creates a tree structure (e.g. depth-first
 * @param {Object} objMap
 * @param {string} parentField
 * @returns {Object[]}
 */
export const sortHierarchically = (objMap, parentField = "parent") => {
  const rv = Object.entries(objMap || {}).map(([id, obj]) => {
    // Construct combined order
    let metadata = { combinedOrder: "", level: 0 };
    // Find this object's parents in order, e.g. parent, grandparent, etc.
    const parentIds = getParentIds(objMap, id, [], parentField);
    let order;
    if (parentIds && parentIds.length > 0) {
      for (const parentId of parentIds) {
        if (objMap[parentId] && objMap[parentId].order != null) {
          order = objMap[parentId].order;
        } else {
          order = 0; // Could also be set as MAX_INT
        }
        metadata.combinedOrder += "." + order.toString().padStart(NUM_PADDING, "0"); // Ensures lexicographical sorting
      }
    }
    // Append current object's order
    if (obj.order != null) {
      order = obj.order;
    } else {
      order = 0;
    }
    metadata.combinedOrder += "." + order.toString().padStart(NUM_PADDING, "0");

    metadata.level = parentIds.length;
    return [id, obj, metadata];
  });
  return rv.sort(([, , a], [, , b]) => a.combinedOrder.localeCompare(b.combinedOrder));
};

export const truncate = (str, maxLength) => {
  if (str.length <= maxLength) {
    return str;
  }
  return str.slice(0, maxLength - 1) + "…";
};

export const isDebugMode = state => {
  const isLocalhost = window.location.hostname === "localhost";
  return isLocalhost || state?.settings?.debugMode === true;
};

/**
 * Traverses a nested object or array, depth first, and calls a function with the key path and value each current level.
 * @param {Object|Array} obj The object or array to traverse.
 * @param {Function} fn The callback function to run. Receives the current path and value as arguments.
 * @param {string} [currentPath] The key path at the current level. Defaults to an empty string.
 */
export const traverse = (obj, fn, currentPath = "", visited = new Set()) => {
  if (!isArray(obj) && !isObject(obj)) {
    return;
  }
  if (visited.has(obj)) {
    // Circular reference detected, skip further traversal
    return;
  }
  visited.add(obj);

  for (const key in obj) {
    const newPath = currentPath ? `${currentPath}.${key}` : key;
    const val = obj[key];

    if (typeof val === "object" && val !== null) {
      // === object also includes arrays
      traverse(val, fn, newPath, visited);
    } else {
      fn(newPath, val);
    }
  }
};

/**
 * Joins path segments into a single path, safely handling undefined or non-string inputs.
 * @param  {...any} paths
 * @returns {string}
 */
export const joinPaths = (...paths) => {
  const filtered = paths.filter(p => typeof p === "string" && p.length > 0);
  if (filtered.length === 0) return "";

  let result = filtered[0];
  for (let i = 1; i < filtered.length; i++) {
    const part = filtered[i];
    if (!result.endsWith("/")) result += "/";
    result += part.startsWith("/") ? part.slice(1) : part;
  }

  // Collapse multiple slashes but preserve protocol (e.g. "https://")
  return result.replace(/([^:]\/)\/+/g, "$1");
};

/**
 * Returns the last segment of a path, safely handling undefined or non-string input.
 * @param {string} path
 * @returns {string|undefined}
 */
export const lastPathSegment = path => {
  if (typeof path !== "string") return undefined;
  return path.split("/").filter(Boolean).pop();
};

export const internalNavigate = (navigateOrHistory, url) => {
  if (!navigateOrHistory || !url) {
    console.error("Invalid arguments for internalNavigate");
    return false;
  }
  try {
    // Resolve the URL relative to the current origin to handle relative paths
    const targetUrl = new URL(url, window.location.origin);

    if (targetUrl.origin === window.location.origin) {
      // Internal navigation: Use React Router navigation
      const path = targetUrl.pathname + targetUrl.search + targetUrl.hash;
      if (typeof navigateOrHistory === "function") {
        navigateOrHistory(path);
      } else {
        navigateOrHistory.push(path);
      }
    } else {
      console.log(`External navigation requested: ${url}`);
      window.location.href = url;
    }
  } catch (error) {
    console.error(`Error processing navigation URL '${url}', falling back to window.location.href:`, error);
    window.location.href = url;
  }
};

/**
 * Exports data to a CSV file that is downloadable in the browser.
 * The column definitions controls what properties are included in the CSV.
 * Each column definition may have:
 * - `header` which is the name of header
 * - `accessor` property. If it's a string, it will be used to access a (nested) property in the data object.
 * The found value will be converted to a string and included in the CSV.
 * If the accessor is a function, it will be called with the current data object and the return value will be used.
 *
 * @param {object[]} data an array of objects to export
 * @param {object[]} columns an array of column definitions.
 * @param {string} filename
 */
export function exportToCsv(data, columns, filename = "export.csv") {
  // Generate CSV header row based on column definitions
  const headers = columns.map(column => column.header).join(",");

  // Generate CSV rows based on column accessors
  const rows = data.map(row => {
    return columns
      .map(column => {
        const value = typeof column.accessor === "function" ? column.accessor(row) : get(row, column.accessor, ""); // Use lodash's get for nested properties

        // Escape double quotes by doubling them
        const escapedValue = String(value).replace(/"/g, '""');
        return `"${escapedValue}"`; // Wrap in quotes
      })
      .join(",");
  });

  // Combine headers and rows into CSV content
  const csvContent = [headers, ...rows].join("\n");

  // Create a Blob and trigger download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function smartConcat(a, b, between = " ") {
  a = String(a || "");
  b = String(b || "");
  between = String(between || "");

  if (!a) return b;
  if (!b) return a;

  // Trim between from end of a and start of b, but only if between is not empty
  if (between && a.endsWith(between)) {
    a = a.slice(0, -between.length);
  }
  if (between && b.startsWith(between)) {
    b = b.slice(between.length);
  }
  // Now smart concat a and b with between inserted
  for (let i = Math.min(a.length, b.length); i > 0; i--) {
    if (a.endsWith(b.slice(0, i))) {
      return a + between + b.slice(i);
    }
  }
  return a + between + b;
}
